import type { FormField } from '@/types/form';

import styles from './GridField.module.css';

interface GridFieldProps {
    field: FormField;
    value: string;
    setValue: (value: string) => void;
    error?: string;
}

function getGrid(field: FormField): { rows: string[]; columns: string[] } {
    const rows = Array.isArray(field.config['rows']) ? (field.config['rows'] as string[]) : [];
    const columns = Array.isArray(field.config['columns']) ? (field.config['columns'] as string[]) : [];
    return { rows, columns };
}

function parseGridValue(value: string): Record<string, string | string[]> {
    if (!value) return {};
    try {
        return JSON.parse(value) as Record<string, string | string[]>;
    } catch {
        return {};
    }
}

export function GridField({ field, value, setValue, error }: GridFieldProps) {
    const { rows, columns } = getGrid(field);
    const isCheckbox = field.field_type === 'checkbox_grid';
    const parsed = parseGridValue(value);

    const handleRadioChange = (row: string, col: string) => {
        const next = { ...parsed, [row]: col };
        setValue(JSON.stringify(next));
    };

    const handleCheckboxChange = (row: string, col: string) => {
        const current = Array.isArray(parsed[row]) ? (parsed[row] as string[]) : [];
        const next = current.includes(col)
            ? current.filter((c) => c !== col)
            : [...current, col];
        setValue(JSON.stringify({ ...parsed, [row]: next }));
    };

    if (rows.length === 0 || columns.length === 0) {
        return <p className={styles.empty}>Sin filas o columnas configuradas</p>;
    }

    return (
        <div className={`${styles.gridWrapper} ${error ? styles.error : ''}`}>
            <div className={styles.gridTable}>
                {/* Header row */}
                <div className={styles.headerRow}>
                    <div className={styles.rowLabel} />
                    {columns.map((col) => (
                        <div key={col} className={styles.colHeader}>{col}</div>
                    ))}
                </div>
                {/* Data rows */}
                {rows.map((row) => (
                    <div key={row} className={styles.dataRow}>
                        <div className={styles.rowLabel}>{row}</div>
                        {columns.map((col) => {
                            if (isCheckbox) {
                                const current = Array.isArray(parsed[row]) ? (parsed[row] as string[]) : [];
                                const checked = current.includes(col);
                                return (
                                    <div key={col} className={styles.cell}>
                                        <input
                                            type="checkbox"
                                            className={styles.cellInput}
                                            checked={checked}
                                            onChange={() => handleCheckboxChange(row, col)}
                                            aria-label={`${row} — ${col}`}
                                        />
                                    </div>
                                );
                            }
                            const selected = parsed[row] === col;
                            return (
                                <div key={col} className={styles.cell}>
                                    <input
                                        type="radio"
                                        className={styles.cellInput}
                                        name={`grid-${field.id}-${row}`}
                                        checked={selected}
                                        onChange={() => handleRadioChange(row, col)}
                                        aria-label={`${row} — ${col}`}
                                    />
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
            {error && <span className={styles.errorMsg} role="alert">{error}</span>}
        </div>
    );
}
